import Callout from "components/callout/Callout";
import { CustomerTeeTimeCard } from "components/customerTeeTimeCard/CustomerTeeTimeCard";
import LoaderCard from "components/loaderCard/LoaderCard";
import Radio from "components/radio";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import React, { useEffect, useMemo, useState } from "react";
import "../clientPortalHome.scss";
import { cartOptions, holeOptions, playerOptions, promotionFilterName, promotionOptions } from "../teeTimeFilters";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { GetCustomerCheckIns, ICheckIns, INotification } from "api/rpc/2024-04/customer/teesheet";
import { IFacility } from "redux/reducers/models/facility";
import axios, { CancelToken } from "axios";
import { IFacilityTeeSheetSettings } from "api/rpc/2024-04/customer/facility";
import { IAPIResponse, StatusCode } from "api/protocols";
import { dequeue, enqueue, showError, showSuccess } from "redux/actions/ui";
import moment from "moment";
import { ITeeTime } from "redux/reducers/models/teetime";
import { convertHoles, convertTime } from "helpers/Helpers";
import ReactDOM from "react-dom";
import { GetGuestCheckIns } from "api/rpc/2024-04/guest/teesheet";
import { IFilterState } from "../ClientPortalHome";
import useModal from "hooks/modals/useModal";
import CustomerLotteryRequestModal from "../CustomerLotteryRequestModal/CustomerLotteryRequestModal";
import { ILottery } from "redux/reducers/models/lottery";
import {
  CustomerGetLottery,
  CustomerPostLotteryRequest,
  IPostLotteryRequestParams,
} from "api/rpc/2024-04/customer/lottery";
import { GetTeeTimesCustomer } from "api/rpc/2022-09/customer/teetime";
import Input from "components/form/input";
import { ButtonNew as Button } from "components/buttonNew";
import Tabs from "components/tabs/Tabs";
import { DEFAULT_CUSTOMER_MAXIMUM_BOOKING_SIZE, getSingleTeeFilterMaxPlayers } from "../../bookingSize";

interface IProps {
  checkTeeTime: (date: string, teeTime: ITeeTime) => Promise<void>;
  dayOfWeekNumber: number;
  filterState: Partial<IFilterState>;
  setFilterState: React.Dispatch<React.SetStateAction<IFilterState>>;
  teeTimeRes: IAPIResponse<Array<ITeeTime>>;
  notifications: Array<INotification>;
  showRestrictedBookings: boolean;
  getTeeTimesAccessCode: (accessCode: string, teesheet: boolean) => Promise<void>;
  leagueSelected?: boolean;
  onCourseSelect: (courseId: number) => void;
}

type BookingSizeSetting = "allow" | "allow_within_group" | "none";

type TeeSheetBookingSettings = Partial<IFacilityTeeSheetSettings> & {
  single_bookings?: BookingSizeSetting;
  twosome_bookings?: BookingSizeSetting;
  threesome_bookings?: BookingSizeSetting;
  foursome_bookings?: BookingSizeSetting;
  max_booking_size?: number;
  customer_maximum_booking_size?: number | null;
};

function getQuantityRemaining(teeTime?: Partial<ITeeTime>) {
  const quantityRemaining = Number(teeTime?.quantity_remaining ?? 0);

  return Number.isFinite(quantityRemaining) ? quantityRemaining : 0;
}

export interface ITeeTimeCard {
  id: number;
  convertedTime: string;
  time: string;
  prepaid: boolean;
  quantity_remaining: number;
  bookable_quantity_remaining: number;
  holes: number[];
  cart_rule: string;
  nine_hole_price: number;
  eighteen_hole_price: number;
  dynamic_percent_variation: number;
  courseName?: string;
  date: string;
  position: number;
  divisionTitle?: string;
  blocked_type?: unknown;
  bookings_prevent_guests: boolean;
  startingHole?: string;
  leagueId: number;
  course_id: number;
  teeTime: ITeeTime;
  is_restricted?: boolean;
  lottery_id?: number;
  promotion_type?: "percent" | "amount" | null;
  promotion_value?: number | null;
  promotion_label?: string | null;
}

interface IState {
  teeTimeCards: Array<ITeeTimeCard>;
  checkInRes: IAPIResponse<Array<ICheckIns>>;
}

export default function ClientPortalTeeTimesList(props: IProps) {
  const {
    checkTeeTime,
    dayOfWeekNumber,
    filterState,
    setFilterState,
    teeTimeRes,
    notifications,
    showRestrictedBookings,
    getTeeTimesAccessCode,
    leagueSelected,
    onCourseSelect,
  } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [accessCode, setAccessCode] = useState<string>("");
  const { clientPortalStore } = useAppSelector(store => store);

  const [state, setState] = useState<IState>({
    teeTimeCards: undefined,
    checkInRes: undefined,
  });

  const {
    state: lotteryRequestModal,
    closeModal: closeLotteryRequestModal,
    updateModal: updateLotteryRequestModal,
  } = useModal<{ lottery: ILottery; lotteryTimes: Array<ITeeTime>; teetime: ITeeTime }>({
    lottery: null,
    lotteryTimes: [],
    teetime: null,
  });

  useEffect(() => {
    const source = axios.CancelToken.source();

    if (teeTimeRes && clientPortalStore?.selectedFacility && clientPortalStore?.teeSheetSettings) {
      const teeTimeCards = setTeeTimeCards(
        teeTimeRes,
        undefined,
        clientPortalStore?.selectedFacility,
        clientPortalStore?.teeSheetSettings,
      );

      ReactDOM.unstable_batchedUpdates(() => {
        setFilterState(prevState => ({
          ...prevState,
          enable_cart_bookings: !!clientPortalStore?.teeSheetSettings?.enable_cart_bookings,
        }));
        setState(prevState => ({ ...prevState, teeTimeCards }));
      });

      // Load check ins for pricing in the background
      void loadCheckIns(
        !!clientPortalStore?.user,
        source.token,
        teeTimeRes,
        clientPortalStore?.selectedFacility,
        clientPortalStore?.teeSheetSettings,
      );
    } else {
      setState(prevState => ({ ...prevState, teeTimeCards: undefined }));
    }

    return () => source.cancel();
  }, [teeTimeRes]);

  useEffect(() => {
    //Update pricing display based on holes filter selection
    if (state?.teeTimeCards !== undefined && teeTimeRes && state?.checkInRes) {
      const teeTimeCards = setTeeTimeCards(
        teeTimeRes,
        state?.checkInRes,
        clientPortalStore?.selectedFacility,
        clientPortalStore?.teeSheetSettings,
      );
      setState(prevState => ({ ...prevState, teeTimeCards: teeTimeCards }));
    }
  }, [filterState.selectedHoles]);

  async function loadCheckIns(
    userActive: boolean,
    cancelToken: CancelToken,
    teeTimesRes: IAPIResponse<Array<ITeeTime>>,
    facility: Partial<IFacility>,
    facilityTeeSheetSettings: IFacilityTeeSheetSettings,
  ) {
    let checkInConfigRes: IAPIResponse<ICheckIns[]> = null;
    const firstTeeTimeDate = teeTimesRes?.data?.[0]?.date;

    if (facilityTeeSheetSettings?.display_online_pricing && firstTeeTimeDate) {
      const checkInParams = {
        facility_id: facility?.id,
        day_of_week_number: dayOfWeekNumber,
        date: moment(firstTeeTimeDate).format("MM-DD"),
        tee_time_date: moment(firstTeeTimeDate).format("YYYY-MM-DD"),
      };

      if (userActive) {
        checkInConfigRes = await GetCustomerCheckIns(checkInParams, false, cancelToken);

        if (checkInConfigRes.status !== StatusCode.OK) {
          if (cancelToken && cancelToken?.reason) {
            return;
          }
          dispatch(showError(t("Error getting pricing")));
        }
      } else {
        checkInConfigRes = await GetGuestCheckIns(checkInParams, false, cancelToken);

        if (checkInConfigRes.status !== StatusCode.OK) {
          if (cancelToken && cancelToken?.reason) {
            return;
          }
          dispatch(showError(t("Error getting pricing")));
        }
      }
    }

    const teeTimeCards = setTeeTimeCards(teeTimesRes, checkInConfigRes, facility, facilityTeeSheetSettings);
    setState(prevState => ({ ...prevState, teeTimeCards, checkInRes: checkInConfigRes }));
  }

  function getBookingSettingBySize(settings: TeeSheetBookingSettings, playerCount: number) {
    switch (playerCount) {
      case 1:
        return settings?.single_bookings ?? "allow";
      case 2:
        return settings?.twosome_bookings ?? "allow";
      case 3:
        return settings?.threesome_bookings ?? "allow";
      case 4:
        return settings?.foursome_bookings ?? "allow";
      default: {
        // No named setting for 5+ — allow when within facility max_booking_size
        const facilityMax = settings?.max_booking_size ?? DEFAULT_CUSTOMER_MAXIMUM_BOOKING_SIZE;
        return playerCount <= facilityMax ? "allow" : "none";
      }
    }
  }

  function isBookingSizeAllowed(
    playerCount: number,
    quantityRemaining: number,
    settings: TeeSheetBookingSettings,
    teeSize?: number,
  ) {
    const safeQuantityRemaining = Number.isFinite(quantityRemaining) ? quantityRemaining : 0;
    const fullTeeSize =
      teeSize != null && Number.isFinite(teeSize) && teeSize > 0 ? teeSize : DEFAULT_CUSTOMER_MAXIMUM_BOOKING_SIZE;

    if (!Number.isFinite(playerCount) || playerCount < 1) {
      return false;
    }

    // Filters / card availability use a single tee time only (not consecutive group capacity)
    if (playerCount > safeQuantityRemaining) {
      return false;
    }

    if (playerCount > (settings?.max_booking_size ?? DEFAULT_CUSTOMER_MAXIMUM_BOOKING_SIZE)) {
      return false;
    }

    const bookingSetting = getBookingSettingBySize(settings, playerCount);

    if (bookingSetting === "none") {
      return false;
    }

    // "Allow within group" means this booking size can only be booked when the
    // tee time already has at least one booked player. A completely open tee time
    // should not advertise that player count yet.
    if (bookingSetting === "allow_within_group") {
      return safeQuantityRemaining < fullTeeSize;
    }

    return true;
  }

  function getBookableQuantityRemaining(
    quantityRemaining: number,
    settings: TeeSheetBookingSettings,
    teeSize?: number,
  ) {
    const safeQuantityRemaining = Number.isFinite(quantityRemaining) ? quantityRemaining : 0;
    const maxOnThisTee = Math.min(
      safeQuantityRemaining,
      settings?.max_booking_size ?? DEFAULT_CUSTOMER_MAXIMUM_BOOKING_SIZE,
    );

    for (let playerCount = maxOnThisTee; playerCount >= 1; playerCount--) {
      if (isBookingSizeAllowed(playerCount, safeQuantityRemaining, settings, teeSize)) {
        return playerCount;
      }
    }

    return 0;
  }

  function filterTeeTimeCards(teeTimeCards: ITeeTimeCard[]) {
    const settings = clientPortalStore?.teeSheetSettings as TeeSheetBookingSettings;
    const selectedPlayers = filterState?.selectedPlayers ? parseInt(filterState.selectedPlayers, 10) : null;

    return teeTimeCards.filter(teeTimeCard => {
      const teeSize = Number(teeTimeCard.teeTime?.size ?? DEFAULT_CUSTOMER_MAXIMUM_BOOKING_SIZE);
      const hasPromotion = Boolean(teeTimeCard.promotion_type) && teeTimeCard.promotion_value != null;
      return (
        (selectedPlayers === null ||
          isBookingSizeAllowed(selectedPlayers, teeTimeCard.quantity_remaining, settings, teeSize)) &&
        (filterState.selectedHoles == null || teeTimeCard.holes.includes(parseInt(filterState.selectedHoles, 10))) &&
        (filterState.selectedCart == null ||
          ((teeTimeCard.cart_rule === "none" || teeTimeCard.cart_rule === "optional") &&
            filterState.selectedCart === "no") ||
          ((teeTimeCard.cart_rule === "required" || teeTimeCard.cart_rule === "optional") &&
            filterState.selectedCart === "yes")) &&
        (filterState.selectedPromotion !== "yes" || hasPromotion) &&
        teeTimeCard.bookable_quantity_remaining > 0 &&
        (showRestrictedBookings || !teeTimeCard?.is_restricted)
      );
    });
  }

  function checkHolesSelected(holes: number) {
    if (filterState.selectedHoles == null) {
      return true;
    }

    switch (filterState.selectedHoles) {
      case "18":
        if (holes === 18) {
          return true;
        }
        break;
      case "9":
        if (holes === 9) {
          return true;
        }
        break;
      case "any":
      case null:
        return true;
    }
    return false;
  }

  function invalidTeeTime(teeTime: ITeeTime) {
    return (
      teeTime?.divisions_available?.length === 1 && teeTime?.divisions_available[0] === 2 && !teeTime?.turn_tee_time_id
    );
  }

  function setTeeTimeCards(
    teeTimesRes: IAPIResponse<Array<ITeeTime>>,
    checkInRes: IAPIResponse<Array<ICheckIns>>,
    facility: Partial<IFacility>,
    facilityTeeSheetSettings: IFacilityTeeSheetSettings,
  ) {
    const teeTimes = teeTimesRes?.data ? [...teeTimesRes.data] : [];
    const teetimeCards: ITeeTimeCard[] = [];
    const checkInsData = checkInRes?.data ? [...checkInRes.data] : checkInRes === undefined ? undefined : [];

    if (teeTimes) {
      for (let i = 0; i < teeTimes?.length; i++) {
        const teetimeData = teeTimes[i];

        if (!teetimeData || invalidTeeTime(teetimeData)) {
          continue;
        }

        const quantityRemaining = getQuantityRemaining(teetimeData);

        if (quantityRemaining < 1) {
          continue;
        }

        const startTime = convertTime(teetimeData.start_time);

        const holes: number[] = (teetimeData.divisions_available ?? [])
          .map(divisionAvailable => {
            if (divisionAvailable === 1) {
              return 9;
            }

            if (divisionAvailable === 2) {
              return 18;
            }

            return null;
          })
          .filter((hole): hole is 9 | 18 => hole !== null);

        const bookableQuantityRemaining = getBookableQuantityRemaining(
          quantityRemaining,
          facilityTeeSheetSettings as TeeSheetBookingSettings,
          Number(teetimeData?.size ?? DEFAULT_CUSTOMER_MAXIMUM_BOOKING_SIZE),
        );

        let eighteenHolePrice: number;
        let nineHolePrice: number;

        if (checkInsData) {
          checkInsData?.every(checkIn => {
            if (eighteenHolePrice && nineHolePrice) {
              return false;
            }

            const startTime = moment(checkIn?.pricing_sheet?.start_time, "HH:mm");
            const endTime = moment(checkIn?.pricing_sheet?.end_time, "HH:mm");
            const checkInCourseId = checkIn?.pricing_sheet?.course_id;

            if (
              (moment(teetimeData?.start_time, "HH:mm").isBetween(startTime, endTime) ||
                moment(teetimeData?.start_time, "HH:mm").isSame(startTime) ||
                moment(teetimeData?.start_time, "HH:mm").isSame(endTime)) &&
              checkHolesSelected(checkIn?.holes) &&
              checkInCourseId === teetimeData?.course_id
            ) {
              if (checkIn?.holes === 18 && holes?.includes(18) && !eighteenHolePrice) {
                let priceOverride = checkIn?.variant?.inventory_levels?.find(
                  level => level?.facility_id === facility?.id,
                )?.price_override;
                if (checkIn.online_price_override) {
                  priceOverride = checkIn.online_price_override;
                }
                eighteenHolePrice = priceOverride ?? checkIn?.variant?.price;
              } else if (checkIn?.holes === 9 && holes?.includes(9) && !nineHolePrice) {
                let priceOverride = checkIn?.variant?.inventory_levels?.find(
                  level => level?.facility_id === facility?.id,
                )?.price_override;
                if (checkIn.online_price_override) {
                  priceOverride = checkIn.online_price_override;
                }
                nineHolePrice = priceOverride ?? checkIn?.variant?.price;
              }
            }
            return true;
          });
        }

        if (bookableQuantityRemaining >= 1) {
          const teetimeCard: ITeeTimeCard = {
            id: teetimeData.id,
            convertedTime: startTime,
            time: teetimeData.start_time,
            prepaid: teeTimes[i].pre_paid_required,
            quantity_remaining: quantityRemaining,
            bookable_quantity_remaining: bookableQuantityRemaining,
            holes: holes,
            cart_rule: teetimeData.cart_rule,
            eighteen_hole_price:
              eighteenHolePrice && !!facilityTeeSheetSettings?.display_online_pricing
                ? eighteenHolePrice
                : checkInsData === undefined
                ? undefined
                : null,
            nine_hole_price:
              nineHolePrice && !!facilityTeeSheetSettings?.display_online_pricing
                ? nineHolePrice
                : checkInsData === undefined
                ? undefined
                : null,
            dynamic_percent_variation: teetimeData.dynamic_percent_variation,
            courseName: facility?.courses?.length > 1 ? teetimeData.course_full_name : undefined,
            date: teetimeData.date,
            position: teetimeData.position,
            divisionTitle: teetimeData.division_title,
            blocked_type: teetimeData.blocked_type,
            startingHole: teetimeData.start_hole,
            leagueId: teetimeData.league_id,
            course_id: teetimeData?.course_id,
            teeTime: teetimeData,
            is_restricted: teetimeData?.is_restricted,
            lottery_id: teetimeData?.lottery_id,
            bookings_prevent_guests: teetimeData?.bookings_prevent_guests,
            promotion_type: teetimeData?.promotion_type,
            promotion_value: teetimeData?.promotion_value,
            promotion_label: teetimeData?.promotion_label,
          };
          teetimeCards.push(teetimeCard);
        }
      }
      return teetimeCards;
    }
  }

  function convertTeeTimeHoles(holes: number[], courseId: number) {
    const course = clientPortalStore.selectedFacility?.courses?.find(course => course.id === courseId);

    return holes.map(hole => convertHoles(hole, course));
  }

  const teeTimeCards = useMemo(() => {
    return state?.teeTimeCards !== undefined
      ? state?.teeTimeCards
        ? filterTeeTimeCards(state?.teeTimeCards)
        : []
      : undefined;
  }, [
    state?.teeTimeCards,
    filterState.selectedCart,
    filterState.selectedHoles,
    filterState.selectedPlayers,
    filterState.selectedPromotion,
    showRestrictedBookings,
    clientPortalStore?.teeSheetSettings,
  ]);

  const hasPromotionsOnDay = useMemo(() => {
    if (state?.teeTimeCards === undefined) {
      return false;
    }

    return state.teeTimeCards.some(
      teeTimeCard => Boolean(teeTimeCard.promotion_type) && teeTimeCard.promotion_value != null,
    );
  }, [state?.teeTimeCards]);

  const promotionName = useMemo(() => promotionFilterName(state?.teeTimeCards ?? []), [state?.teeTimeCards]);

  useEffect(() => {
    if (state?.teeTimeCards === undefined) {
      return;
    }

    if (!hasPromotionsOnDay && filterState.selectedPromotion != null) {
      setFilterState(prevState => ({ ...prevState, selectedPromotion: null }));
    }
  }, [hasPromotionsOnDay, filterState.selectedPromotion, setFilterState, state?.teeTimeCards]);

  async function getLottery(teetime: ITeeTimeCard) {
    //Cannot request time in the past
    if (moment().isAfter(moment(teetime?.time, "HH:mm"))) {
      dispatch(showError(t("Cannot request a tee time in the past")));
      return;
    }

    dispatch(enqueue());

    const teetimeRes = await GetTeeTimesCustomer(
      {
        date: teetime?.date,
        id: teetime?.id,
        facility_id: clientPortalStore?.selectedFacility?.id,
        turn_tee_time: true,
        extended: true,
      },
      false,
    );

    if (teetimeRes?.status !== StatusCode.OK) {
      dispatch(dequeue());
      dispatch(showError(t("Error getting tee time")));
      return;
    }

    const res = await CustomerGetLottery(
      { id: teetime?.lottery_id, facility_id: clientPortalStore?.selectedFacility?.id },
      false,
    );

    if (res?.status !== StatusCode.OK) {
      dispatch(dequeue());
      dispatch(showError(t("Error getting lottery details")));
      return;
    }

    dispatch(dequeue());

    const lotteryTeeTimes = teeTimeRes?.data?.filter(time => time?.lottery_id === teetime?.lottery_id);

    updateLotteryRequestModal({
      isOpen: true,
      lottery: res?.data,
      lotteryTimes: lotteryTeeTimes,
      teetime: teetimeRes?.data[0],
    });
  }

  async function createLotteryRequest(params: IPostLotteryRequestParams) {
    const res = await CustomerPostLotteryRequest(params, true);

    if (res?.status !== StatusCode.OK) {
      dispatch(showError(t(typeof res?.data === "string" ? res?.data : "Error creating lottery request")));
      return;
    }

    dispatch(showSuccess(t("Successfully created tee time lottery request")));
    void closeLotteryRequestModal();
  }

  function handleEnterKeydown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.code === "Enter" || e.code === "NumpadEnter") {
      void getTeeTimesAccessCode(accessCode, false);
    }
  }

  async function handleTeeTimeCardClick(teetime: ITeeTimeCard, isLottery: boolean) {
    const latestTeeTime = teeTimeRes?.data?.find(time => time?.id === teetime?.id) ?? teetime?.teeTime;
    const quantityRemaining = getQuantityRemaining(latestTeeTime);

    if (!latestTeeTime || quantityRemaining < 1) {
      dispatch(showError(t("This tee time is no longer available. Please refresh tee times.")));
      return;
    }

    const settings = clientPortalStore?.teeSheetSettings as TeeSheetBookingSettings;
    const bookableQuantityRemaining = getBookableQuantityRemaining(
      quantityRemaining,
      settings,
      Number(latestTeeTime?.size ?? DEFAULT_CUSTOMER_MAXIMUM_BOOKING_SIZE),
    );

    if (bookableQuantityRemaining < 1) {
      dispatch(showError(t("This tee time can no longer be booked with the current booking settings.")));
      return;
    }

    if (isLottery) {
      await getLottery({
        ...teetime,
        teeTime: latestTeeTime,
        quantity_remaining: quantityRemaining,
        bookable_quantity_remaining: bookableQuantityRemaining,
      });
      return;
    }

    await checkTeeTime(latestTeeTime?.date ?? teetime?.date, latestTeeTime);
  }

  const coursesTabs = [
    { content: t("All"), id: -1 },
    ...(clientPortalStore?.selectedFacility?.courses?.map(course => ({ content: course?.long_name, id: course?.id })) ??
      []),
  ];

  const maxPlayersAvailable = useMemo(() => {
    const teeTimes = teeTimeRes?.data ?? [];
    return getSingleTeeFilterMaxPlayers(
      teeTimes.map(t => getQuantityRemaining(t)),
      clientPortalStore?.teeSheetSettings?.max_booking_size,
    );
  }, [teeTimeRes, clientPortalStore?.teeSheetSettings?.max_booking_size]);

  return (
    <>
      {/* Desktop filters */}
      <div className="client-portal-home-filter-section">
        <div className="client-portal-home-filters">
          {hasPromotionsOnDay ? (
            <div>
              <div className="client-portal-home-filter-header">{t("Promotions")}</div>
              <Radio.Group
                name="promotions"
                onChange={(promotions: string) =>
                  setFilterState(prevState => ({
                    ...prevState,
                    selectedPromotion: promotions === "any" ? null : promotions,
                  }))
                }
                value={filterState.selectedPromotion ?? "any"}
              >
                {promotionOptions(t, promotionName).map(promotionOption => {
                  return (
                    <Radio.Button key={`promotion_${promotionOption.value}`} value={promotionOption.value}>
                      {promotionOption.title}
                    </Radio.Button>
                  );
                })}
              </Radio.Group>
            </div>
          ) : null}

          <div>
            <div className="client-portal-home-filter-header">{"Number of Players"}</div>
            <Radio.Group
              name="players"
              onChange={(players: string) =>
                setFilterState(prevState => ({ ...prevState, selectedPlayers: players === "any" ? null : players }))
              }
              value={filterState.selectedPlayers ?? "any"}
            >
              {playerOptions(
                t,
                clientPortalStore?.teeSheetSettings?.single_bookings === "none",
                maxPlayersAvailable,
              ).map(player => {
                const playerCount = player.value !== "any" ? parseInt(player.value, 10) : null;
                const playerDisabled =
                  player?.disabled ||
                  (playerCount !== null &&
                    getBookingSettingBySize(
                      clientPortalStore?.teeSheetSettings as TeeSheetBookingSettings,
                      playerCount,
                    ) === "none");

                return (
                  <Radio.Button key={`players_${player.value}`} value={player.value} disabled={playerDisabled}>
                    {player.title}
                  </Radio.Button>
                );
              })}
            </Radio.Group>
          </div>

          <div>
            <div className="client-portal-home-filter-header">{t("Holes")}</div>
            <Radio.Group
              name="holes"
              onChange={(holes: string) =>
                setFilterState(prevState => ({ ...prevState, selectedHoles: holes === "any" ? null : holes }))
              }
              value={filterState.selectedHoles ?? "any"}
            >
              {holeOptions(t, clientPortalStore?.selectedFacility?.courses[0]).map(hole => {
                return (
                  <Radio.Button key={`holes_${hole.value}`} value={hole.value}>
                    <span>{hole.title}</span>
                    {hole.value !== "any" ? (
                      <FontAwesomeIcon
                        className="client-portal-home-filter-golf-icon"
                        icon={["far", "golf-flag-hole"]}
                      />
                    ) : null}
                  </Radio.Button>
                );
              })}
            </Radio.Group>
          </div>

          {filterState?.enable_cart_bookings ? (
            <div>
              <div className="client-portal-home-filter-header">{t("Cart")}</div>
              <Radio.Group
                name="carts"
                onChange={(carts: string) =>
                  setFilterState(prevState => ({ ...prevState, selectedCart: carts === "any" ? null : carts }))
                }
                value={filterState.selectedCart ?? "any"}
              >
                {cartOptions(t).map(cartOption => {
                  return (
                    <Radio.Button key={`cart_${cartOption.value}`} value={cartOption.value}>
                      {cartOption.title}
                    </Radio.Button>
                  );
                })}
              </Radio.Group>
            </div>
          ) : null}
        </div>
      </div>

      <div className="client-portal-home-tee-time-section">
        {clientPortalStore?.selectedFacility?.courses?.length > 1 ? (
          <div style={{ width: "fit-content" }}>
            <Tabs
              variant="underline"
              tabs={coursesTabs}
              selected={coursesTabs?.findIndex(course => course?.id === filterState?.selectedCourseId) ?? 0}
              onSelect={tabIndex => onCourseSelect(coursesTabs[tabIndex]?.id)}
            />
            <br />
          </div>
        ) : null}

        <div>
          {(notifications ?? []).map((notification, index) => {
            return <Callout key={index} type="info" title={notification.subject} markdown={notification.content} />;
          })}

          {state?.teeTimeCards?.length === 0 && (
            <>
              <Callout type="error" title={t("No Tee Times")} content={t("There are no tee times for this day")} />
              {clientPortalStore?.teeSheetSettings?.tee_times_access_code &&
              clientPortalStore?.user &&
              !leagueSelected ? (
                <>
                  <br />
                  <Input
                    label={t("Access Code")}
                    tooltipText={t("Enter an access code to access exclusive tee times")}
                    placeholder={t("Access Code")}
                    value={accessCode}
                    onKeyDown={handleEnterKeydown}
                    autoFocus
                    onChange={e => setAccessCode(e.target.value)}
                    trailingButtons={[
                      <Button key={1} type="secondary" onClick={() => getTeeTimesAccessCode(accessCode, false)}>
                        {t("Enter")}
                      </Button>,
                    ]}
                  />
                </>
              ) : null}
            </>
          )}

          {state?.teeTimeCards && state?.teeTimeCards?.length !== 0 && teeTimeCards?.length === 0 && (
            <Callout
              type="error"
              title={t("No Tee Times")}
              content={t("There are no tee times that match the selected filters")}
            />
          )}
        </div>

        <div className="client-portal-home-tee-times">
          {teeTimeCards === undefined
            ? [...Array(3)]?.map((_, index) => {
                return (
                  <LoaderCard
                    key={index}
                    textRows={4}
                    customCSSProperties={{ width: "100%", height: "190px" }}
                    textRowSpacing={25}
                  />
                );
              })
            : teeTimeCards?.map(teetime => {
                let courseName;
                if (clientPortalStore?.selectedFacility?.courses?.length > 1) {
                  courseName = "Course: " + teetime.courseName;
                }

                const isLottery = teetime?.blocked_type === "lottery" && teetime?.lottery_id != null;

                return (
                  <div key={`${teetime.id}_${teetime.date}_${teetime.time}`}>
                    <CustomerTeeTimeCard
                      time={teetime.convertedTime}
                      isPrepaid={teetime.prepaid}
                      isShotgun={teetime.blocked_type === "shotgun"}
                      spotsRemaining={teetime.bookable_quantity_remaining}
                      holes={convertTeeTimeHoles(teetime.holes, teetime.course_id)}
                      cartRule={filterState?.enable_cart_bookings ? teetime.cart_rule : "none"}
                      eighteenHolePrice={!isLottery ? teetime.eighteen_hole_price : null}
                      nineHolePrice={!isLottery ? teetime.nine_hole_price : null}
                      dynamicPercentVariation={teetime.dynamic_percent_variation}
                      onClick={() => void handleTeeTimeCardClick(teetime, isLottery)}
                      courseName={clientPortalStore?.selectedFacility?.courses?.length > 1 ? courseName : null}
                      guestsAllowed={teetime.bookings_prevent_guests}
                      divisionTitle={teetime.divisionTitle}
                      singleBookings={clientPortalStore?.teeSheetSettings?.single_bookings}
                      twosomeBookings={clientPortalStore?.teeSheetSettings?.twosome_bookings}
                      threesomeBookings={clientPortalStore?.teeSheetSettings?.threesome_bookings}
                      foursomeBookings={clientPortalStore?.teeSheetSettings?.foursome_bookings}
                      quantityRemaining={teetime.quantity_remaining}
                      teeSize={Number(teetime.teeTime?.size ?? teetime.quantity_remaining)}
                      startingHole={teetime.startingHole}
                      isLeague={!!teetime.leagueId}
                      restricted={teetime?.is_restricted}
                      isLottery={isLottery}
                      displayDivision={clientPortalStore?.teeSheetSettings?.display_divisions_online}
                      promotionType={teetime.promotion_type}
                      promotionValue={teetime.promotion_value}
                      promotionLabel={teetime.promotion_label}
                    />
                  </div>
                );
              })}
        </div>
      </div>

      {/* Lottery Request Modal */}
      <CustomerLotteryRequestModal
        open={lotteryRequestModal?.isOpen}
        onCancel={closeLotteryRequestModal}
        onOk={createLotteryRequest}
        facility={clientPortalStore?.selectedFacility}
        lottery={lotteryRequestModal?.lottery}
        lotteryTimes={lotteryRequestModal?.lotteryTimes}
        teetime={lotteryRequestModal?.teetime}
      />
    </>
  );
}
